"use client";

import { BrandMark } from "@/components/brand";
import { ChevronLeft, X } from "lucide-react";
import { useOnboarding } from "./OnboardingContext";

interface QuizProgressBarProps {
  onBack?: () => void;
  onClose?: () => void;
  showBack?: boolean;
}

export function QuizProgressBar({ onBack, onClose, showBack = true }: QuizProgressBarProps) {
  const { progress, currentStep, goToPreviousStep, totalSteps } = useOnboarding();

  const handleBack = () => {
    if (onBack) {
      onBack();
    } else if (currentStep > 0) {
      goToPreviousStep();
    }
  };

  return (
    <div className="sticky top-0 z-50 border-b border-white/10 bg-[#0F0F14]/95 backdrop-blur-sm">
      <div className="mx-auto max-w-2xl px-4 sm:px-6">
        <div className="flex h-16 items-center justify-between">
          {showBack && currentStep > 0 ? (
            <button
              type="button"
              onClick={handleBack}
              className="-ml-2 flex min-h-11 min-w-11 items-center gap-1 rounded-lg p-2 text-gray-400 transition-colors hover:bg-white/5 hover:text-white focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F]"
              aria-label="Go back"
            >
              <ChevronLeft className="w-5 h-5" />
              <span className="text-sm font-medium hidden sm:inline">Back</span>
            </button>
          ) : (
            <div className="w-16" /> // Spacer
          )}

          <div className="flex items-center gap-2">
            <BrandMark size={32} className="h-8 w-8 object-contain" alt="" priority />
            <div>
              <div className="text-sm font-bold text-white">ChickyTutor</div>
              <div className="text-[11px] font-medium text-[#AEAEB2]">
                Step {currentStep + 1} of {totalSteps}
              </div>
            </div>
          </div>

          {onClose ? (
            <button
              type="button"
              onClick={onClose}
              className="-mr-2 flex min-h-11 min-w-11 items-center justify-center rounded-lg p-2 text-gray-500 transition-colors hover:bg-white/5 hover:text-gray-300 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#FD954F]"
              aria-label="Close"
            >
              <X className="w-5 h-5" />
            </button>
          ) : (
            <div className="w-16" /> // Spacer
          )}
        </div>
      </div>

      <div
        className="h-1 bg-[#1B1C22]"
        role="progressbar"
        aria-label="Onboarding progress"
        aria-valuemin={0}
        aria-valuemax={100}
        aria-valuenow={Math.round(progress)}
      >
        <div
          className="h-full bg-[#FD954F] transition-[width] duration-500 ease-out motion-reduce:transition-none"
          style={{ width: `${progress}%` }}
        />
      </div>
    </div>
  );
}
